/**
 * Note Metadata Component
 *
 * Displays patient assignment and encounter information below the NoteTopBar.
 *
 * Features:
 * - 80px height with #FCFCFC background
 * - Patient assignment or patient name display with pronouns
 * - Encounter date, start time, and duration from encounter data (with fallback to transcription data)
 * - Editable encounter time when data comes from encounter (not transcription)
 * - Horizontal layout with dividers between items
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AssignNoteToPatient from '@components/AssignNoteToPatient';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { cleanCptCodes, CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker/ICD10CodePicker';
import { EligibilityWarningIcon } from '@features/eligibility-checks';
import { useUpdateEncounterMutation } from '@features/encounters';
import { notesApi } from '@features/notes';
import {
  PatientAllergiesPopover,
  PatientDiagnosesEditModal,
  PatientDiagnosesPopover,
  PatientIndicators,
  PatientLastEncounterPopover,
  PatientMedicationsEditModal,
  PatientMedicationsPopover,
  useIndicatorHover,
  usePatientContext,
} from '@features/patient-context-bar';
import { useAccessToken } from '@features/user';
import { UserIcon } from '@icons/UserIcon';
import {
  AttachMoney,
  CalendarToday,
  Check as CheckIcon,
  Close as CloseIcon,
  LocalHospital,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import type { EncounterResponse } from '@store/generated/encounters';
import type { NoteResponse } from '@store/generated/notes';
import { useAppDispatch, useModal } from '@store/hooks';
import type { Patient } from '@ts/types';
import { formatPatientName } from '@utils/patientUtils';
import { formatDate } from '@utils/utils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { LAYOUT_SPACING } from '../constants/layout';

// Quick duration options (matching appointment components)
const QUICK_DURATIONS = [
  { label: '30m', minutes: 30 },
  { label: '45m', minutes: 45 },
  { label: '1h', minutes: 60 },
];

export interface NoteMetadataProps {
  /** Note ID for patient assignment */
  noteId: string;
  /** Note data with patient and transcription information */
  noteData?: NoteResponse;
  /** Encounter data with start_time, end_time, and duration */
  encounterData?: EncounterResponse;
  /** Whether patient assignment/unassignment is allowed */
  editable?: boolean;
  /** AI-suggested CPT codes from audit analysis (shown as ghost when no codes exist) */
  suggestedCptCodes?: string[];
  /** Callback when user clicks to apply suggested CPT codes */
  onUseSuggestedCodes?: () => void;
  /** Callback when viewing last encounter note from patient context */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when CPT codes are changed (for triggering reaudit) */
  onCptCodesChanged?: (codes: string[]) => void;
}

/**
 * Metadata item display component
 * Shows icon + text with tooltip
 */
const MetadataItem: React.FC<{
  icon: React.ReactNode;
  text: string;
  tooltip: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  clickable?: boolean;
}> = ({ icon, text, tooltip, onClick, clickable = false }) => {
  const theme = useTheme();

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: theme.palette.text.secondary,
          ...(clickable && {
            cursor: 'pointer',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            mx: -1,
            my: -0.5,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }),
        }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          sx: { flexShrink: 0 },
          fontSize: 'small',
        })}
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Note Metadata Bar Component
 */
export const NoteMetadata: React.FC<NoteMetadataProps> = ({
  noteId,
  noteData,
  encounterData,
  editable = true,
  suggestedCptCodes,
  onUseSuggestedCodes,
  onViewLastEncounterNote,
  onCptCodesChanged,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const { enqueueSnackbar } = useSnackbar();
  const { note } = useEventTracking();
  const { open: openModal } = useModal();

  // State for hover and modal
  const [isHovering, setIsHovering] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  // State for encounter time editing
  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState<string>('');
  const [isCustomDuration, setIsCustomDuration] = useState(false);

  // State for CPT codes editing
  const [cptAnchorEl, setCptAnchorEl] = useState<HTMLElement | null>(null);

  // State for ICD-10 codes editing
  const [icd10AnchorEl, setIcd10AnchorEl] = useState<HTMLElement | null>(null);

  // RTK Query mutation for updating encounter
  const [updateEncounter] = useUpdateEncounterMutation();

  // Check if patient is assigned
  const patient = noteData?.patient;

  // Handler to open insurance modal
  const handleAddInsurance = useCallback(() => {
    if (patient?.patient_id) {
      openModal(MODAL_TYPES.INSURANCE_EDIT, {
        patientId: patient.patient_id,
      });
    }
  }, [patient?.patient_id, openModal]);
  const hasPatient = Boolean(patient);
  const encounterId = noteData?.encounter?.encounter_id;

  // Patient context data and hover state
  const {
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
  } = usePatientContext(patient?.patient_id || null);

  // Hover state for each indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();

  // Modal state for patient context editing
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);

  // Determine if time data comes from encounter (editable) or transcription (read-only)
  const hasEncounterTimeData = Boolean(
    encounterData?.start_time || encounterData?.appointment?.start_time,
  );

  // Initialize time editor state when popover opens
  useEffect(() => {
    if (timeAnchorEl && encounterData) {
      // Get the start time from encounter data
      const encounterStartTime = encounterData.start_time || encounterData.appointment?.start_time;
      if (encounterStartTime) {
        const startDate = new Date(encounterStartTime);
        setSelectedDate(format(startDate, 'yyyy-MM-dd'));
        setStartTime(format(startDate, 'HH:mm'));
      }

      // Calculate duration in minutes
      let duration = 0;
      if (encounterData.start_time && encounterData.end_time) {
        const start = new Date(encounterData.start_time).getTime();
        const end = new Date(encounterData.end_time).getTime();
        duration = Math.floor((end - start) / (1000 * 60));
      } else if (encounterData.appointment?.duration) {
        duration = encounterData.appointment.duration;
      }

      // Check if duration matches a quick preset
      const isPresetDuration = QUICK_DURATIONS.some(preset => preset.minutes === duration);
      setIsCustomDuration(!isPresetDuration && duration > 0);
      setDurationMinutes(duration > 0 ? String(duration) : '');
    }
  }, [timeAnchorEl, encounterData]);

  /**
   * Handle saving encounter time changes
   * Uses optimistic update pattern - closes popover immediately while mutation runs in background
   */
  const handleSaveEncounterTime = useCallback(() => {
    if (!encounterId || !selectedDate || !startTime) return;

    const startDateTime = new Date(`${selectedDate}T${startTime}`);
    const duration = parseInt(durationMinutes, 10) || 0;
    const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000);

    // Track event
    note.encounterTimeSaved({
      note_id: noteId,
      encounter_id: encounterId,
      duration_minutes: duration,
      is_custom_duration: isCustomDuration,
    });

    // Close popover immediately (optimistic)
    setTimeAnchorEl(null);

    // Fire mutation - RTK Query handles optimistic cache update automatically
    // The encountersApi has onQueryStarted that updates cache immediately
    updateEncounter({
      encounterId,
      encounterUpdate: {
        start_time: startDateTime.toISOString(),
        end_time: duration > 0 ? endDateTime.toISOString() : null,
      },
    })
      .unwrap()
      .catch((error: unknown) => {
        console.error('Error updating encounter time:', error);
        enqueueSnackbar('Failed to update encounter time', { variant: 'error' });
      });
  }, [
    encounterId,
    selectedDate,
    startTime,
    durationMinutes,
    isCustomDuration,
    noteId,
    note,
    updateEncounter,
    enqueueSnackbar,
  ]);

  /**
   * Handle opening time editor popover
   */
  const handleOpenTimeEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterTimeEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
      });
      setTimeAnchorEl(event.currentTarget);
    },
    [noteId, encounterId, note],
  );

  /**
   * Handle closing time editor popover
   */
  const handleCloseTimeEditor = useCallback(() => {
    setTimeAnchorEl(null);
  }, []);

  /**
   * Handle quick duration selection
   */
  const handleQuickDurationSelect = useCallback((minutes: number) => {
    setDurationMinutes(String(minutes));
    setIsCustomDuration(false);
  }, []);

  /**
   * Handle custom duration selection
   */
  const handleCustomDurationSelect = useCallback(() => {
    setIsCustomDuration(true);
  }, []);

  const isTimeEditorOpen = Boolean(timeAnchorEl);
  const canSaveTime = selectedDate && startTime;
  const selectedDuration = parseInt(durationMinutes, 10) || 0;

  /**
   * Handle opening CPT editor popover
   */
  const handleOpenCptEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterCptEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: Boolean(encounterData?.cpt_codes?.length),
      });
      setCptAnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.cpt_codes?.length, note],
  );

  /**
   * Handle CPT codes change - auto-save immediately
   * CPTCodePicker now only fires onChange when codes are committed (not on typing)
   * Triggers reaudit after successful save if callback is provided
   */
  const handleCptChange = useCallback(
    (codes: string[]) => {
      if (!encounterId) return;

      // Clean CPT codes
      const cleanedCodes = cleanCptCodes(codes);

      // Track event
      note.encounterCptCodesSaved({
        note_id: noteId,
        encounter_id: encounterId,
        cpt_codes: cleanedCodes,
        cpt_codes_count: cleanedCodes.length,
      });

      // Fire mutation - RTK Query handles optimistic cache update automatically
      updateEncounter({
        encounterId,
        encounterUpdate: {
          cpt_codes: cleanedCodes,
        },
      })
        .unwrap()
        .then(() => {
          // Trigger reaudit after successful CPT code update
          if (onCptCodesChanged) {
            onCptCodesChanged(cleanedCodes);
          }
        })
        .catch((error: unknown) => {
          console.error('Error updating CPT codes:', error);
          enqueueSnackbar('Failed to update CPT codes', { variant: 'error' });
        });
    },
    [encounterId, noteId, note, updateEncounter, enqueueSnackbar, onCptCodesChanged],
  );

  /**
   * Handle closing CPT editor popover
   */
  const handleCloseCptEditor = useCallback(() => {
    setCptAnchorEl(null);
  }, []);

  const isCptEditorOpen = Boolean(cptAnchorEl);

  /**
   * Handle ICD-10 codes change - auto-save immediately
   * ICD10CodePicker only fires onChange when codes are added/removed (not on typing)
   */
  const handleIcd10Change = useCallback(
    (codes: string[]) => {
      if (!encounterId) return;

      // Filter empty codes
      const filtered = codes.filter(c => c && c.trim() !== '');

      // Track event
      note.encounterIcd10CodesSaved?.({
        note_id: noteId,
        encounter_id: encounterId,
        icd10_codes: filtered,
        icd10_codes_count: filtered.length,
      });

      // Fire mutation - RTK Query handles optimistic cache update automatically
      updateEncounter({
        encounterId,
        encounterUpdate: {
          icd10_codes: filtered,
        },
      })
        .unwrap()
        .catch((error: unknown) => {
          console.error('Error updating ICD-10 codes:', error);
          enqueueSnackbar('Failed to update ICD-10 codes', { variant: 'error' });
        });
    },
    [encounterId, noteId, note, updateEncounter, enqueueSnackbar],
  );

  /**
   * Handle opening ICD-10 editor popover
   */
  const handleOpenIcd10Editor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterIcd10EditorOpened?.({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: Boolean(encounterData?.icd10_codes?.length),
      });
      setIcd10AnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.icd10_codes?.length, note],
  );

  /**
   * Handle closing ICD-10 editor popover
   */
  const handleCloseIcd10Editor = useCallback(() => {
    setIcd10AnchorEl(null);
  }, []);

  const isIcd10EditorOpen = Boolean(icd10AnchorEl);

  /**
   * Handle patient assignment with optimistic update
   * Updates RTK Query cache for immediate UI feedback
   */
  const handleAssignPatient = useCallback(
    (assignedPatient: Patient) => {
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
          draft.patient = assignedPatient as any;
          draft.patient_id = assignedPatient.patient_id;
        }),
      );
    },
    [dispatch, noteId],
  );

  /**
   * Handle patient assignment error
   * Invalidates cache to refetch fresh data
   */
  const handleAssignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment error
   * Invalidates cache to refetch fresh data
   */
  const handleUnassignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment with optimistic update
   * Removes patient from RTK Query cache and calls API
   */
  const handleUnassign = useCallback(async () => {
    // Close modal immediately
    setShowUnassignModal(false);

    // Optimistically update cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        draft.patient = null;
        draft.patient_id = null;
      }),
    );

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: `/patient/unassignpatient`,
        accessToken,
        body: {
          note_id: noteId,
        },
      });

      if (response.ok) {
        enqueueSnackbar('Patient unassigned successfully', {
          variant: 'success',
        });
        // Track successful unassignment
        note.patientUnassigned(noteId, {
          patient_id: patient?.patient_id,
          encounter_id: encounterId,
        });
      } else {
        throw new Error('Failed to unassign patient');
      }
    } catch (error) {
      console.error('Error unassigning patient:', error);
      enqueueSnackbar('Failed to unassign patient', { variant: 'error' });

      // On error, invalidate cache to refetch correct data
      handleUnassignError();
    }
  }, [dispatch, noteId, getAccessToken, enqueueSnackbar, handleUnassignError]);

  /**
   * Navigate to patient page
   */
  const handlePatientClick = useCallback(() => {
    if (patient?.patient_id) {
      note.patientNameClicked(noteId, {
        patient_id: patient.patient_id,
        encounter_id: encounterId,
      });
      navigate(`/patient/${patient.patient_id}`);
    }
  }, [navigate, patient, noteId, encounterId, note]);

  /**
   * Handle unassign icon click
   */
  const handleUnassignClick = useCallback(() => {
    note.patientUnassignClicked(noteId, {
      patient_id: patient?.patient_id,
      encounter_id: encounterId,
    });
    setShowUnassignModal(true);
  }, [patient, noteId, encounterId, note]);

  /**
   * Handle medications indicator click
   */
  const handleMedicationsClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setMedicationsModalOpen(true);
  }, [patient?.patient_id]);

  /**
   * Handle diagnoses indicator click
   */
  const handleDiagnosesClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setDiagnosesModalOpen(true);
  }, [patient?.patient_id]);

  /**
   * Handle medications updated callback
   */
  const handleMedicationsUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    // Data will be refetched automatically by usePatientContext
  }, [patient?.patient_id]);

  /**
   * Handle diagnoses updated callback
   */
  const handleDiagnosesUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    // Data will be refetched automatically by usePatientContext
  }, [patient?.patient_id]);

  // Determine if encounter time is editable (data comes from encounter, not transcription)
  const isTimeEditable = editable && hasEncounterTimeData && Boolean(encounterId);

  // Determine if CPT codes are editable (only if we have encounter data)
  const isCptEditable = editable && Boolean(encounterId && encounterData);
  const hasCptCodes = Boolean(
    encounterData?.cpt_codes &&
      encounterData.cpt_codes.length > 0 &&
      encounterData.cpt_codes.some(c => c && c.trim() !== ''),
  );

  // Build metadata items array
  const metadataItems: Array<{
    icon: React.ReactNode;
    text: string;
    tooltip: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    clickable?: boolean;
  }> = [];

  // Determine start time with fallback chain:
  // 1. encounterData.start_time (from separate useGetEncounterQuery hook)
  // 2. encounterData.appointment.start_time (if encounter doesn't have start_time directly)
  // 3. noteData.transcription_start_timestamp (fallback if encounter data unavailable)
  let startTimeEpoch: number | null = null;
  if (encounterData?.start_time) {
    // Encounter start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
  } else if (encounterData?.appointment?.start_time) {
    // Appointment start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
  } else if (noteData?.transcription_start_timestamp) {
    // Transcription timestamp is already in epoch seconds
    startTimeEpoch = noteData.transcription_start_timestamp;
  }

  // Calculate duration with fallback chain:
  // 1. Calculate from encounter start_time and end_time
  // 2. Use appointment duration (if encounter times not available)
  // 3. Fall back to transcription_length_seconds
  let durationSeconds: number | null = null;
  if (encounterData?.start_time && encounterData?.end_time) {
    // Calculate from encounter start/end times
    const start = new Date(encounterData.start_time).getTime();
    const end = new Date(encounterData.end_time).getTime();
    durationSeconds = Math.floor((end - start) / 1000);
  } else if (encounterData?.appointment?.duration) {
    // Appointment duration is in minutes, convert to seconds
    durationSeconds = encounterData.appointment.duration * 60;
  } else if (noteData?.transcription_length_seconds) {
    // Fall back to transcription length
    durationSeconds = noteData.transcription_length_seconds;
  }

  // Add encounter date/time as a single clickable item if available
  if (startTimeEpoch) {
    const startTimeFormatted = formatDate(startTimeEpoch, 'time').toLowerCase();
    // Use short month format (e.g., "Dec 11, 2025" instead of "December 11, 2025")
    const startTimeDate = new Date(startTimeEpoch * 1000);
    const dateText = format(startTimeDate, 'MMM d, yyyy');

    let timeText = '';
    if (durationSeconds) {
      // Format as time range with duration (e.g., "11:30 am – 12:15 pm, 45 min")
      const endTimeFormatted = formatDate(startTimeEpoch + durationSeconds, 'time').toLowerCase();

      // Format duration concisely
      const hours = Math.floor(durationSeconds / 3600);
      const minutes = Math.floor((durationSeconds % 3600) / 60);

      let durationText = '';
      if (hours > 0) {
        durationText = `${hours} hr${hours > 1 ? 's' : ''}`;
        if (minutes > 0) {
          durationText += ` ${minutes} min`;
        }
      } else if (minutes > 0) {
        durationText = `${minutes} min`;
      } else {
        durationText = `${Math.floor(durationSeconds)} sec`;
      }

      timeText = `${startTimeFormatted} – ${endTimeFormatted}, ${durationText}`;
    } else {
      timeText = startTimeFormatted;
    }

    metadataItems.push({
      icon: <CalendarToday />,
      text: `${dateText} • ${timeText}`,
      tooltip: isTimeEditable ? 'Click to edit encounter date and time' : 'Encounter Date and Time',
      onClick: isTimeEditable ? handleOpenTimeEditor : undefined,
      clickable: isTimeEditable,
    });
  }

  // Check if we have suggested codes to show as ghost
  const hasSuggestedCodes = Boolean(
    suggestedCptCodes &&
      suggestedCptCodes.length > 0 &&
      suggestedCptCodes.some(c => c && c.trim() !== ''),
  );
  const filteredSuggestedCodes = suggestedCptCodes?.filter(c => c && c.trim() !== '') || [];

  // Add CPT codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (hasCptCodes || (isCptEditable && !hasSuggestedCodes)) {
    if (hasCptCodes) {
      // Show existing CPT codes (clickable only when editable)
      const cptText = encounterData?.cpt_codes?.filter(c => c && c.trim() !== '').join(', ') || '';
      metadataItems.push({
        icon: <AttachMoney />,
        text: cptText,
        tooltip: isCptEditable ? 'Click to edit CPT codes' : 'CPT codes',
        onClick: isCptEditable ? handleOpenCptEditor : undefined,
        clickable: isCptEditable,
      });
    } else if (isCptEditable && !hasSuggestedCodes) {
      // No codes and no suggestions - show prompt to enter codes (only when editable)
      metadataItems.push({
        icon: <AttachMoney />,
        text: 'CPT',
        tooltip: 'Add CPT codes',
        onClick: handleOpenCptEditor,
        clickable: true,
      });
    }
    // Note: When hasSuggestedCodes is true and !hasCptCodes, we render ghost codes separately below
  }

  // Determine if ICD-10 codes are editable (only if we have encounter data)
  const isIcd10Editable = editable && Boolean(encounterId && encounterData);
  const hasIcd10Codes = Boolean(
    encounterData?.icd10_codes &&
      encounterData.icd10_codes.length > 0 &&
      encounterData.icd10_codes.some(c => c && c.trim() !== ''),
  );

  // Add ICD-10 codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (hasIcd10Codes || isIcd10Editable) {
    if (hasIcd10Codes) {
      // Show existing ICD-10 codes (clickable only when editable)
      const icd10Text =
        encounterData?.icd10_codes?.filter(c => c && c.trim() !== '').join(', ') || '';
      metadataItems.push({
        icon: <LocalHospital />,
        text: icd10Text,
        tooltip: isIcd10Editable
          ? 'Click to edit ICD-10 diagnosis codes'
          : 'ICD-10 diagnosis codes',
        onClick: isIcd10Editable ? handleOpenIcd10Editor : undefined,
        clickable: isIcd10Editable,
      });
    } else if (isIcd10Editable) {
      // No codes - show prompt to enter codes (only when editable)
      metadataItems.push({
        icon: <LocalHospital />,
        text: 'ICD-10',
        tooltip: 'Add diagnosis codes',
        onClick: handleOpenIcd10Editor,
        clickable: true,
      });
    }
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCFCFC',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container
        maxWidth="xxl"
        sx={{
          px: LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          py: 2,
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {/* Patient Assignment or Display */}
        {!hasPatient ? (
          editable && (
            <AssignNoteToPatient
              noteId={noteId}
              encounterId={encounterId}
              onAssign={handleAssignPatient}
              onAssignError={handleAssignError}
            />
          )
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            onMouseEnter={() => editable && setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Patient name, pronouns, eligibility icon, and context indicators - all on same line */}
            {editable && (
              <IconButton size="small" onClick={handleUnassignClick} sx={{ p: 0.5 }}>
                {isHovering ? (
                  <CloseIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : (
                  <UserIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                )}
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                onClick={handlePatientClick}
                sx={{
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                  textDecoration: isHovering ? 'underline' : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPatientName(
                  patient?.first_name,
                  patient?.last_name,
                  (patient as any)?.preferred_name,
                )}
                {/* Conditionally display pronouns if available */}
                {(patient as any)?.pronouns && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    ({(patient as any).pronouns})
                  </Typography>
                )}
              </Typography>
              <EligibilityWarningIcon
                patientId={patient?.patient_id}
                source="note_metadata"
                onAddInsurance={handleAddInsurance}
              />
            </Box>
            {/* Patient context indicators on same line */}
            {patient && (
              <PatientIndicators
                medicationsHover={medicationsHover}
                diagnosesHover={diagnosesHover}
                allergiesHover={allergiesHover}
                lastEncounterHover={lastEncounterHover}
                onMedicationsClick={handleMedicationsClick}
                onDiagnosesClick={handleDiagnosesClick}
              />
            )}
          </Box>
        )}

        {/* Metadata Items */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1,
            '& > *:not(:last-child)::after': {
              content: '""',
              display: 'inline-block',
              width: '1px',
              height: 24,
              bgcolor: theme.palette.divider,
              ml: 2,
            },
          }}
        >
          {metadataItems.map((item, index) => (
            <MetadataItem
              key={index}
              icon={item.icon}
              text={item.text}
              tooltip={item.tooltip}
              onClick={item.onClick}
              clickable={item.clickable}
            />
          ))}
        </Box>

        {/* Ghost Suggested CPT Codes - shown when no codes exist but suggestions are available */}
        {isCptEditable && !hasCptCodes && hasSuggestedCodes && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 2,
              pl: 2,
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tooltip
              title="Click to edit CPT codes. JotPsych suggests these codes based on your note content"
              arrow
            >
              <Box
                onClick={handleOpenCptEditor}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  mx: -1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <AttachMoney
                  fontSize="small"
                  sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {filteredSuggestedCodes.map((code, idx) => (
                    <Chip
                      key={idx}
                      label={code}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        opacity: 0.6,
                        borderStyle: 'dashed',
                        borderColor: theme.palette.text.disabled,
                        color: theme.palette.text.secondary,
                        pointerEvents: 'none', // Prevent chip clicks from interfering
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Tooltip>
            {onUseSuggestedCodes && (
              <Tooltip title="Accept suggested codes" arrow>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation(); // Prevent opening the CPT picker
                    onUseSuggestedCodes();
                  }}
                  sx={{
                    p: 0.5,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Encounter Time Editor Popover */}
        <Popover
          open={isTimeEditorOpen}
          anchorEl={timeAnchorEl}
          onClose={handleCloseTimeEditor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">Encounter Time</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    When did this encounter take place?
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseTimeEditor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                type="date"
                label="Date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && canSaveTime) {
                    e.preventDefault();
                    handleSaveEncounterTime();
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                type="time"
                label="Start Time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && canSaveTime) {
                    e.preventDefault();
                    handleSaveEncounterTime();
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={!selectedDate}
              />

              {/* Quick duration chips */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Duration:
                </Typography>
                {QUICK_DURATIONS.map(duration => (
                  <Chip
                    key={duration.minutes}
                    label={duration.label}
                    onClick={() => handleQuickDurationSelect(duration.minutes)}
                    color={
                      selectedDuration === duration.minutes && !isCustomDuration
                        ? 'primary'
                        : 'default'
                    }
                    variant={
                      selectedDuration === duration.minutes && !isCustomDuration
                        ? 'filled'
                        : 'outlined'
                    }
                    size="small"
                  />
                ))}
                <Chip
                  label="Other"
                  onClick={handleCustomDurationSelect}
                  color={isCustomDuration ? 'primary' : 'default'}
                  variant={isCustomDuration ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>

              {/* Custom duration input - only show when "Other" is selected */}
              {isCustomDuration && (
                <TextField
                  type="number"
                  label="Duration (min)"
                  value={durationMinutes}
                  onChange={e => setDurationMinutes(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && canSaveTime) {
                      e.preventDefault();
                      handleSaveEncounterTime();
                    }
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!selectedDate}
                  inputProps={{ min: 0 }}
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Button
                  variant="text"
                  onClick={handleCloseTimeEditor}
                  sx={{ color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEncounterTime}
                  disabled={!canSaveTime}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </Box>
        </Popover>

        {/* CPT Codes Editor Popover */}
        <Popover
          open={isCptEditorOpen}
          anchorEl={cptAnchorEl}
          onClose={handleCloseCptEditor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">CPT Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to two CPT codes for this encounter (optional).
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseCptEditor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <CPTCodePicker
                value={encounterData?.cpt_codes || []}
                onChange={handleCptChange}
                showHeader={false}
                suggestedCodes={suggestedCptCodes}
                onUseSuggestedCodes={
                  onUseSuggestedCodes
                    ? () => {
                        onUseSuggestedCodes();
                        // Close the popover after applying suggested codes
                        setCptAnchorEl(null);
                      }
                    : undefined
                }
              />
            </Stack>
          </Box>
        </Popover>

        {/* ICD-10 Codes Editor Popover */}
        <Popover
          open={isIcd10EditorOpen}
          anchorEl={icd10AnchorEl}
          onClose={handleCloseIcd10Editor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">ICD-10 Diagnosis Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to 4 diagnosis codes. The first code is the primary diagnosis.
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseIcd10Editor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <ICD10CodePicker
                value={encounterData?.icd10_codes || []}
                onChange={handleIcd10Change}
                showHeader={false}
              />
            </Stack>
          </Box>
        </Popover>

        {/* Unassignment Confirmation Modal */}
        <ConfirmDialog
          open={showUnassignModal}
          variant="warning"
          title="Unassign Patient"
          message={`Are you sure you want to unassign ${formatPatientName(
            patient?.first_name,
            patient?.last_name,
            (patient as any)?.preferred_name,
          )} from this note?`}
          onClose={() => setShowUnassignModal(false)}
          onConfirm={handleUnassign}
          confirmText="Unassign"
        />
      </Container>

      {/* Patient Context Popovers */}
      {patient && (
        <>
          {/* Medications Popover */}
          <PatientMedicationsPopover
            medications={medications}
            anchorEl={medicationsHover.anchorEl}
            open={medicationsHover.isHovered && !!medicationsHover.anchorEl}
            onClose={() => {
              medicationsHover.handlePopoverLeave();
            }}
            onMouseEnter={medicationsHover.handlePopoverEnter}
            onMouseLeave={medicationsHover.handlePopoverLeave}
            isLoading={loadingState.medications}
            onEdit={handleMedicationsClick}
          />

          {/* Diagnoses Popover */}
          <PatientDiagnosesPopover
            diagnoses={diagnoses}
            anchorEl={diagnosesHover.anchorEl}
            open={diagnosesHover.isHovered && !!diagnosesHover.anchorEl}
            onClose={() => {
              diagnosesHover.handlePopoverLeave();
            }}
            onMouseEnter={diagnosesHover.handlePopoverEnter}
            onMouseLeave={diagnosesHover.handlePopoverLeave}
            isLoading={loadingState.diagnoses}
            onEdit={handleDiagnosesClick}
          />

          {/* Allergies Popover */}
          <PatientAllergiesPopover
            allergies={allergies}
            anchorEl={allergiesHover.anchorEl}
            open={allergiesHover.isHovered && !!allergiesHover.anchorEl}
            onClose={() => {
              allergiesHover.handlePopoverLeave();
            }}
            onMouseEnter={allergiesHover.handlePopoverEnter}
            onMouseLeave={allergiesHover.handlePopoverLeave}
            isLoading={loadingState.allergies}
          />

          {/* Last Encounter Popover */}
          <PatientLastEncounterPopover
            data={lastEncounter}
            anchorEl={lastEncounterHover.anchorEl}
            open={lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl}
            onClose={() => {
              lastEncounterHover.handlePopoverLeave();
            }}
            onMouseEnter={lastEncounterHover.handlePopoverEnter}
            onMouseLeave={lastEncounterHover.handlePopoverLeave}
            isLoading={loadingState.lastEncounter}
            onViewNote={onViewLastEncounterNote}
          />

          {/* Medications Edit Modal */}
          {patient.patient_id && medicationsModuleInfo && (
            <PatientMedicationsEditModal
              open={medicationsModalOpen}
              onClose={() => setMedicationsModalOpen(false)}
              patientId={patient.patient_id}
              medications={medications}
              moduleInfo={medicationsModuleInfo}
              onMedicationsUpdated={handleMedicationsUpdated}
            />
          )}

          {/* Diagnoses Edit Modal */}
          {patient.patient_id && diagnosesModuleInfo && (
            <PatientDiagnosesEditModal
              open={diagnosesModalOpen}
              onClose={() => setDiagnosesModalOpen(false)}
              patientId={patient.patient_id}
              diagnoses={diagnoses}
              moduleInfo={diagnosesModuleInfo}
              onDiagnosesUpdated={handleDiagnosesUpdated}
            />
          )}
        </>
      )}
    </Box>
  );
};
